/**
 * ImageSearchForm Component
 * 
 * Search form with keyword input, multi-select source checkboxes, and search button.
 * All sources can be freely combined (stock + AI together).
 * Nothing is preselected — the user picks what they want.
 */

import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import type { ImageSearchFormProps, ImageSource } from './types';
import { IMAGE_SOURCES, MIN_QUERY_LENGTH, hasAnyAISource, hasAnyStockSource, THEME_COLORS } from './constants';

export const ImageSearchForm: React.FC<ImageSearchFormProps> = ({
  query,
  onQueryChange,
  selectedSources,
  onSourcesChange,
  onSearch,
  isLoading,
  disabled = false,
}) => {
  const isSearchDisabled = disabled || isLoading || query.trim().length < MIN_QUERY_LENGTH || selectedSources.length === 0;
  const hasAI = hasAnyAISource(selectedSources);
  const hasStock = hasAnyStockSource(selectedSources);

  // Button text adapts to selection
  const buttonText = hasAI && hasStock ? 'Search & Generate' : hasAI ? 'Generate' : 'Search';
  const ButtonIcon = hasAI && !hasStock ? AIIcon : SearchIcon;

  // Input label adapts: if ONLY AI is selected show prompt label, otherwise generic
  const onlyAI = hasAI && !hasStock;
  const inputLabel = onlyAI ? 'Describe the image you want' : 'Search keywords';
  const inputPlaceholder = onlyAI
    ? 'A sunset over mountains with dramatic clouds...'
    : 'e.g., business meeting, nature...';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSearchDisabled) {
      onSearch();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSearchDisabled) {
      onSearch();
    }
  };

  /**
   * Simple toggle — add or remove the source, no mutual exclusivity.
   */
  const handleToggle = (value: ImageSource) => {
    if (selectedSources.includes(value)) {
      onSourcesChange(selectedSources.filter(s => s !== value));
    } else {
      onSourcesChange([...selectedSources, value]);
    }
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      color: THEME_COLORS.textPrimary,
      backgroundColor: THEME_COLORS.cardBackground,
      '& fieldset': { borderColor: THEME_COLORS.border },
      '&:hover fieldset': { borderColor: THEME_COLORS.borderHover },
      '&.Mui-focused fieldset': { borderColor: THEME_COLORS.accent },
    },
    '& .MuiInputLabel-root': {
      color: THEME_COLORS.textSecondary,
      '&.Mui-focused': { color: THEME_COLORS.accent },
    },
  };

  const checkboxSx = {
    color: THEME_COLORS.textMuted,
    '&.Mui-checked': { color: THEME_COLORS.accent },
    padding: '4px',
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Source checkboxes — flat list, all equal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: THEME_COLORS.textMuted,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mr: 1,
          }}
        >
          Sources
        </Typography>

        {IMAGE_SOURCES.map((s) => {
          const checked = selectedSources.includes(s.value);
          const isAI = s.category === 'ai';
          return (
            <FormControlLabel
              key={s.value}
              control={
                <Checkbox
                  checked={checked}
                  onChange={() => handleToggle(s.value)}
                  disabled={isLoading}
                  size="small"
                  sx={checkboxSx}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isAI && (
                    <AIIcon sx={{ fontSize: 16, color: checked ? THEME_COLORS.accent : THEME_COLORS.textMuted }} />
                  )}
                  <Typography variant="body2" sx={{ color: checked ? THEME_COLORS.textPrimary : THEME_COLORS.textSecondary }}>
                    {s.label}
                  </Typography>
                </Box>
              }
              sx={{ mr: 0.5 }}
            />
          );
        })}

      </Box>

      {/* Search input + button row */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
        }}
      >
        {/* Search Input */}
        <TextField
          label={inputLabel}
          placeholder={inputPlaceholder}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          fullWidth
          sx={{
            flex: 1,
            minWidth: 200,
            ...inputStyles,
          }}
          helperText={
            query.length > 0 && query.length < MIN_QUERY_LENGTH
              ? `Enter at least ${MIN_QUERY_LENGTH} characters`
              : undefined
          }
          FormHelperTextProps={{
            sx: { color: THEME_COLORS.textMuted },
          }}
        />

        {/* Search/Generate Button */}
        <Button
          type="submit"
          variant="contained"
          disabled={isSearchDisabled}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ButtonIcon />}
          sx={{
            height: 56,
            minWidth: 150,
            bgcolor: THEME_COLORS.accent,
            color: THEME_COLORS.background,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              bgcolor: THEME_COLORS.accentHover,
            },
            '&.Mui-disabled': {
              bgcolor: THEME_COLORS.border,
              color: THEME_COLORS.textMuted,
            },
          }}
        >
          {isLoading ? 'Loading...' : buttonText}
        </Button>
      </Box>
    </Box>
  );
};

export default ImageSearchForm;
